import type { JSX } from 'react';
import { Button, Field, Input, Select } from '@/components/ui';
import { CONDITION_LABEL } from '@/lib/marketing';
import type { CampaignCondition, MarketingCampaign, StepInput } from '@/lib/types';
import {
  MAX_DELAY_DAYS,
  MAX_DELAY_HOURS,
  MAX_STEPS,
  stepPath,
  variantBApplies,
  variantPath,
  type StepKind,
} from './step-rules';
import { VariantEditor, type VariantTemplateOption } from './VariantEditor';

export interface VariantDraft {
  key: string;
  id: number | null;
  label: 'A' | 'B';
  subject: string;
  preheader: string;
  templateId: number | null;
}

export interface StepDraft {
  key: string;
  id: number | null;
  stepOrder: number;
  delayDays: number;
  delayHours: number;
  condition: CampaignCondition;
  variants: VariantDraft[];
}

export interface SequenceStepsEditorProps {
  kind: StepKind;
  abEnabled: boolean;
  steps: StepDraft[];
  onChange(steps: StepDraft[]): void;
  templates: VariantTemplateOption[];
  disabled?: boolean;
  /** Keyed like the server's detail paths, e.g. `steps.0.variants.1.subject`. */
  errors?: Record<string, string>;
}

const CONDITIONS: readonly CampaignCondition[] = [
  'all',
  'not_opened',
  'not_clicked',
  'opened',
  'clicked',
];

function newKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function blankVariant(label: 'A' | 'B'): VariantDraft {
  return { key: newKey(), id: null, label, subject: '', preheader: '', templateId: null };
}

function blankStep(stepOrder: number): StepDraft {
  return {
    key: newKey(),
    id: null,
    stepOrder,
    delayDays: stepOrder === 1 ? 0 : 1,
    delayHours: 0,
    condition: 'all',
    variants: [blankVariant('A')],
  };
}

/** Renumber `stepOrder` from position, and pin step 1 to no wait. */
function renumber(steps: StepDraft[]): StepDraft[] {
  return steps.map((step, i) =>
    i === 0
      ? { ...step, stepOrder: 1, delayDays: 0, delayHours: 0 }
      : { ...step, stepOrder: i + 1 },
  );
}

/** A then B, so variant indices match what `stepsToInput` sends. */
function orderVariants(variants: VariantDraft[]): VariantDraft[] {
  return [...variants].sort((a, b) => a.label.localeCompare(b.label));
}

export function stepsFromCampaign(c: MarketingCampaign): StepDraft[] {
  const steps = [...c.steps]
    .sort((a, b) => a.stepOrder - b.stepOrder)
    .map<StepDraft>((step) => ({
      key: newKey(),
      id: step.id,
      stepOrder: step.stepOrder,
      delayDays: step.delayDays,
      delayHours: step.delayHours,
      condition: step.condition,
      variants: orderVariants(
        step.variants.map((v) => ({
          key: newKey(),
          id: v.id,
          label: v.label,
          subject: v.subject,
          preheader: v.preheader ?? '',
          templateId: v.template?.id ?? null,
        })),
      ),
    }));
  return steps.length > 0 ? steps : [blankStep(1)];
}

/**
 * Drafts → the `PUT /steps` body. Keys and ids are dropped; an empty preheader
 * is sent as `null`.
 *
 * Pass `options` to drop the B variant wherever A/B does not apply (every step
 * of a sequence, any broadcast with A/B off) — the server refuses a B it was
 * not asked for, and a draft keeps its B text across an A/B toggle.
 */
export function stepsToInput(
  steps: StepDraft[],
  options?: { kind: StepKind; abEnabled: boolean },
): StepInput[] {
  return steps.map((step, i) => ({
    stepOrder: i + 1,
    delayDays: i === 0 ? 0 : step.delayDays,
    delayHours: i === 0 ? 0 : step.delayHours,
    condition: i === 0 ? 'all' : step.condition,
    variants: orderVariants(step.variants)
      .filter(
        (v) => v.label === 'A' || !options || variantBApplies(options.kind, options.abEnabled, i),
      )
      .map((v) => ({
        label: v.label,
        subject: v.subject.trim(),
        preheader: v.preheader.trim() === '' ? null : v.preheader.trim(),
        templateId: v.templateId,
      })),
  }));
}

function parseWhole(raw: string, max: number): number {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, max);
}

const ICON_PROPS = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function UpIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="m18 15-6-6-6 6" />
    </svg>
  );
}
function DownIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
function RemoveIcon() {
  return (
    <svg {...ICON_PROPS}>
      <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
    </svg>
  );
}

/**
 * The emails a campaign sends. A broadcast is one email (two side by side when
 * A/B is on); a sequence is a timeline of up to ten, each waiting on the one
 * before.
 */
export function SequenceStepsEditor({
  kind,
  abEnabled,
  steps,
  onChange,
  templates,
  disabled,
  errors = {},
}: SequenceStepsEditorProps): JSX.Element {
  // A broadcast always edits exactly one step, whatever it was handed.
  const shown = kind === 'broadcast' ? steps.slice(0, 1) : steps;
  const list = shown.length > 0 ? shown : [blankStep(1)];

  function commit(next: StepDraft[]) {
    onChange(renumber(next));
  }

  function patchStep(index: number, patch: Partial<StepDraft>) {
    commit(list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function patchVariant(
    index: number,
    label: 'A' | 'B',
    patch: Partial<Omit<VariantDraft, 'key' | 'id' | 'label'>>,
  ) {
    const step = list[index];
    if (!step) return;
    const exists = step.variants.some((v) => v.label === label);
    const variants = exists
      ? step.variants.map((v) => (v.label === label ? { ...v, ...patch } : v))
      : orderVariants([...step.variants, { ...blankVariant(label), ...patch }]);
    patchStep(index, { variants });
  }

  function move(index: number, by: -1 | 1) {
    const target = index + by;
    if (target < 0 || target >= list.length) return;
    const a = list[index];
    const b = list[target];
    if (!a || !b) return;
    const next = [...list];
    next[index] = b;
    next[target] = a;
    commit(next);
  }

  function remove(index: number) {
    if (index === 0) return;
    commit(list.filter((_, i) => i !== index));
  }

  function add() {
    if (list.length >= MAX_STEPS) return;
    commit([...list, blankStep(list.length + 1)]);
  }

  function variantEditor(index: number, label: 'A' | 'B', heading?: string) {
    const step = list[index];
    if (!step) return null;
    const vIndex = label === 'A' ? 0 : 1;
    const variant = step.variants.find((v) => v.label === label) ?? {
      ...blankVariant(label),
      key: `${step.key}-${label}`,
    };
    return (
      <VariantEditor
        key={variant.key}
        variant={variant}
        heading={heading}
        templates={templates}
        disabled={disabled}
        onChange={(patch) => patchVariant(index, label, patch)}
        errors={{
          subject: errors[variantPath(index, vIndex, 'subject')],
          preheader: errors[variantPath(index, vIndex, 'preheader')],
          templateId: errors[variantPath(index, vIndex, 'templateId')],
        }}
      />
    );
  }

  const listError = errors.steps;

  if (kind === 'broadcast') {
    const withB = variantBApplies(kind, abEnabled, 0);
    return (
      <div className="grid min-w-0 gap-3">
        {listError && (
          <p role="alert" className="m-0 text-xs text-danger">
            {listError}
          </p>
        )}
        {withB ? (
          <div className="grid min-w-0 gap-6 lg:grid-cols-2 [&>*]:min-w-0">
            {variantEditor(0, 'A', 'Variant A')}
            {variantEditor(0, 'B', 'Variant B')}
          </div>
        ) : (
          variantEditor(0, 'A')
        )}
      </div>
    );
  }

  return (
    <div className="grid min-w-0 gap-3">
      {listError && (
        <p role="alert" className="m-0 text-xs text-danger">
          {listError}
        </p>
      )}
      <ol className="m-0 grid min-w-0 list-none gap-0 p-0">
        {list.map((step, index) => {
          const first = index === 0;
          const last = index === list.length - 1;
          const delayError =
            errors[stepPath(index, 'delayHours')] ?? errors[stepPath(index, 'delayDays')];
          const conditionError = errors[stepPath(index, 'condition')];
          return (
            <li key={step.key} className="relative grid min-w-0 grid-cols-[1.75rem_1fr] gap-3">
              <div className="flex flex-col items-center">
                <span
                  aria-hidden="true"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-border bg-surface-raised text-xs font-semibold text-fg-secondary"
                >
                  {index + 1}
                </span>
                {!last && <span aria-hidden="true" className="w-px flex-1 bg-border" />}
              </div>

              <section
                aria-label={`Email ${index + 1}`}
                className="mb-4 grid min-w-0 gap-3 rounded-md border border-border bg-surface p-3"
              >
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                  <h3 className="m-0 text-sm font-semibold text-fg">Email {index + 1}</h3>
                  {!disabled && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        aria-label={`Move email ${index + 1} up`}
                        tooltip="Move up"
                        disabled={index <= 1}
                        onClick={() => move(index, -1)}
                      >
                        <UpIcon />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        aria-label={`Move email ${index + 1} down`}
                        tooltip="Move down"
                        disabled={first || last}
                        onClick={() => move(index, 1)}
                      >
                        <DownIcon />
                      </Button>
                      {!first && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          aria-label={`Remove email ${index + 1}`}
                          tooltip="Remove"
                          onClick={() => remove(index)}
                        >
                          <RemoveIcon />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {first ? (
                  <p className="m-0 text-xs text-fg-muted">Sends at the scheduled time</p>
                ) : (
                  <div className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)]">
                    <Field label="Wait (days)" error={delayError}>
                      {({ id, 'aria-describedby': describedBy }) => (
                        <Input
                          id={id}
                          aria-describedby={describedBy}
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={MAX_DELAY_DAYS}
                          value={step.delayDays}
                          invalid={Boolean(delayError)}
                          disabled={disabled}
                          onChange={(e) =>
                            patchStep(index, { delayDays: parseWhole(e.target.value, MAX_DELAY_DAYS) })
                          }
                        />
                      )}
                    </Field>
                    <Field label="Wait (hours)">
                      {({ id }) => (
                        <Input
                          id={id}
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={MAX_DELAY_HOURS}
                          value={step.delayHours}
                          invalid={Boolean(delayError)}
                          disabled={disabled}
                          onChange={(e) =>
                            patchStep(index, {
                              delayHours: parseWhole(e.target.value, MAX_DELAY_HOURS),
                            })
                          }
                        />
                      )}
                    </Field>
                    <Field label="Send to" error={conditionError}>
                      {({ id, 'aria-describedby': describedBy }) => (
                        <Select
                          id={id}
                          aria-describedby={describedBy}
                          value={step.condition}
                          invalid={Boolean(conditionError)}
                          disabled={disabled}
                          onChange={(e) =>
                            patchStep(index, { condition: e.target.value as CampaignCondition })
                          }
                        >
                          {CONDITIONS.map((c) => (
                            <option key={c} value={c}>
                              {CONDITION_LABEL[c]}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>
                    <p className="m-0 text-xs text-fg-muted sm:col-span-3">
                      Wait {step.delayDays} {step.delayDays === 1 ? 'day' : 'days'} {step.delayHours}{' '}
                      {step.delayHours === 1 ? 'hour' : 'hours'} after the previous email
                    </p>
                  </div>
                )}

                {variantEditor(index, 'A')}
              </section>
            </li>
          );
        })}
      </ol>

      {!disabled && (
        <div>
          <Button variant="secondary" onClick={add} disabled={list.length >= MAX_STEPS}>
            Add email
          </Button>
        </div>
      )}
    </div>
  );
}
